import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

/**
 * useAsyncJob — start an async LLM job and poll until done.
 *
 *   const { start, job, busy, error, reset } = useAsyncJob();
 *   await start(() => strategyApi.assess(matter.id, { extra_notes }));
 *   // job.status: queued → running → completed | failed
 *   // job.stages[] for live timeline
 *   // job.result for the final payload (when status==='completed')
 */
export function useAsyncJob({ intervalMs = 2000, maxPolls = 90 } = {}) {
    const [job, setJob] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const timerRef = useRef(null);

    const stopPolling = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    useEffect(() => stopPolling, [stopPolling]);

    const reset = useCallback(() => {
        stopPolling();
        setJob(null);
        setBusy(false);
        setError(null);
    }, [stopPolling]);

    const start = useCallback(async (submitFn) => {
        reset();
        setBusy(true);
        try {
            const { job_id } = await submitFn();
            if (!job_id) {
                setBusy(false);
                setError("No job_id returned");
                return null;
            }
            let polls = 0;
            return await new Promise((resolve) => {
                const tick = async () => {
                    polls += 1;
                    try {
                        const j = await api.get(`/jobs/${job_id}`).then((r) => r.data);
                        setJob(j);
                        if (j.status === "completed") {
                            setBusy(false);
                            resolve(j);
                            return;
                        }
                        if (j.status === "failed") {
                            setBusy(false);
                            setError(j.error || "Job failed");
                            resolve(j);
                            return;
                        }
                        if (polls >= maxPolls) {
                            setBusy(false);
                            setError("Polling timed out");
                            resolve(j);
                            return;
                        }
                    } catch (e) {
                        setBusy(false);
                        setError(e?.response?.data?.detail || e.message);
                        resolve(null);
                        return;
                    }
                    timerRef.current = setTimeout(tick, intervalMs);
                };
                tick();
            });
        } catch (e) {
            setBusy(false);
            setError(e?.response?.data?.detail || e.message);
            return null;
        }
    }, [reset, intervalMs, maxPolls]);

    return { job, busy, error, start, reset };
}
